/**
 * @file BootRetry.ts
 * @role The boot-time retry loop: run a loading task; on failure show a retryable error on
 *       the given surface (the splash retry panel), record a diagnostic code, and loop until
 *       the task succeeds. Extracted from Bootstrapper so asset-bundle and RGS-session
 *       failures share one tested mechanism with different diagnostics areas.
 * @owns The retry loop + error normalization to a GameError fallback.
 * @reads A RetrySurface (show/clear error), a diagnostics recorder, the task.
 * @neverDoes Render. Decide boot ordering (Bootstrapper owns that). Swallow failures.
 * @invariants 21 (boot always reaches a terminal state: success or a live retry affordance).
 * @see src/app/Bootstrapper.ts, src/scenes/SplashScene/SplashScene.ts
 */
import { GameError, type GameErrorCode } from '@core/errors/GameError';

/** The minimal error-panel contract the retry loop drives (the splash implements it). */
export interface RetrySurface {
  showError(message: string, onRetry: () => void): void;
  clearError(): void;
}

export interface RetryFallback {
  code: GameErrorCode;
  message: string;
}

/**
 * Wrap an already-in-flight attempt so it overlaps other boot work: the FIRST call consumes
 * the pre-started promise; every later call (a user retry after failure) starts fresh. The
 * pre-started rejection is marked handled here so a failure that happens while other boot
 * work is still running never surfaces as an unhandled rejection — it is re-thrown when the
 * retry loop awaits the first call.
 */
export function usePreStartedAttempt<T>(
  preStarted: Promise<T>,
  fresh: () => Promise<T>,
): () => Promise<T> {
  let pending: Promise<T> | undefined = preStarted;
  void preStarted.catch(() => undefined);
  return () => {
    const attempt = pending ?? fresh();
    pending = undefined;
    return attempt;
  };
}

/**
 * Run `task`; on a failure show the retry panel and wait for the user, then loop.
 * Never resolves without success, never hangs without a visible retry (Invariant 21).
 */
export async function loadWithRetry(
  surface: RetrySurface,
  recordFailure: (code: string) => void,
  fallback: RetryFallback,
  task: () => Promise<void>,
): Promise<void> {
  for (;;) {
    try {
      await task();
      surface.clearError();
      return;
    } catch (error) {
      const gameError =
        error instanceof GameError
          ? error
          : new GameError(fallback.code, fallback.message, { cause: error });
      recordFailure(gameError.code);
      await new Promise<void>((resolve) => {
        surface.showError(`${gameError.message}\nTap retry to try again.`, () => resolve());
      });
    }
  }
}
